import * as React from "react";
import { Input, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@/components/bp/bp-ui";
import { CaseClient, EvidenceClient, LabClient } from "@app/api2/api";
import { makeCancelable } from "@common/Utils";
import { chunk, groupBy, isNumber, max, xor } from "lodash";

interface IMarkerSpecificSettingsEditorProps {
    // kitId?: number;

    labId?: number;
    caseId?: number;
    evidenceId?: number;
    defaultValue?: string;

    name: string;
    readOnly: boolean;

    value?: string;
    onChange?: (value: string) => void;
    onMarkersValueChange?: (markers: string[], value: any) => void;
    onMarkerValueChange?: (marker: string, value: any) => void;

}

type Marker = {
    name: string,
    color: string,
    value: any
}

type MarkerDye = {
    key: string;
    color: string;
    value: any;
    systems: Marker[]
}

export const MarkerSpecificSettingsEditor: React.FunctionComponent<IMarkerSpecificSettingsEditorProps> = (props) => {
    const t = window.translation;
    const evidenceClient = new EvidenceClient();
    const caseClientClient = new CaseClient();
    // const labClient = new LabClient();
    const [systemList, setSystemList] = React.useState([] as Array<Marker>);
    const [markerRows, setMarkerRows] = React.useState([] as Array<MarkerDye>);

    const fetchMarkers = async () => {
        if (props.labId && props.caseId && props.evidenceId) {
            let systems = (await evidenceClient.getKitMarkers(props.labId, props.caseId, props.evidenceId)).map((k) => ({ name: k.name, value: JSON.parse(props.defaultValue ?? 'null'), color: k.color }));
            return systems;
        } else if (props.labId && props.caseId) {
            let systems = (await caseClientClient.getKitMarkers(props.labId, props.caseId)).map((k) => ({ name: k.name, value: JSON.parse(props.defaultValue ?? 'null'), color: k.color }));
            return systems;
        } else {
            return []
        }
    }

    React.useEffect(() => {
        // console.log(props);
        var query = makeCancelable(fetchMarkers());

        
        query.promise.then(kitMarkers => {
            const parsedValue = props.value ? JSON.parse(props.value) : {};

            // console.log(kitMarkers);

            // set marker values
            kitMarkers.forEach(x => {
                // console.log(x);
                if (Object.keys(parsedValue).indexOf(x.name) > -1)
                    x.value = parsedValue[x.name]
                // else if (isNumber(parsedDefaultValue))
                //     x.value = parsedDefaultValue
                else if (isNumber(parsedValue))
                    x.value = parsedValue
            });


            let kitMarkers2 = kitMarkers.length ? kitMarkers : Object.keys(parsedValue).map(x => {
                return {
                    name: x,
                    value: parsedValue[x],
                    color: 'black' // not important
                }
            })

            setSystemList(kitMarkers2);
        }).catch((reason) => { 
            // console.log("Marker fetch cancelled or failed", reason);
        });

        return () => {
            query.cancel();
        }
    }, [props.labId, props.caseId, props.evidenceId, props.value])

    const firstRender = React.useRef(true);

    React.useEffect(() => {
        const first = firstRender.current;
        if (firstRender.current)
            firstRender.current = false;

        const systemsByColor = groupBy(systemList, x => x.color);

        const markerChunks = () => chunk(systemList, 5);

        const firstTime = markerRows.length == 0;

        const rows = Object.keys(systemsByColor).length > 1
            ? Object.keys(systemsByColor).map((color, idx) => { return { key: `${color}_${idx}`, value: props.defaultValue, color: color, systems: systemsByColor[color] }; })
            : markerChunks().map((chunk, idx) => { return { key: `${Object.keys(systemsByColor)[0]}_${idx}`, value: props.defaultValue, color: Object.keys(systemsByColor)[0], systems: chunk } })

        setMarkerRows(rows.map(row => { return { ...row, value: firstTime ? props.defaultValue : markerRows.find(x => x.key == row.key)?.value } }))

        if (!first)
            if (props.onChange)
                props.onChange(JSON.stringify(systemList.reduce((prev, curr) => { prev[curr.name] = curr.value; return prev; }, {})))

    }, [systemList])

    const onChangeMarkerValue = (value, markerName: string) => {
        const newArr = [...systemList];
        const indexOf = newArr.findIndex(x => x.name == markerName);
        newArr[indexOf].value = value;
        setSystemList(newArr)

        if (props.onMarkerValueChange)
            props.onMarkerValueChange(markerName, value)
    }

    // const copyFromDefault = () => {
    //     [...systemList].forEach((item, i) => {
    //         onChangeMarkerValue(props.defaultValue, item.name);
    //     })
    // }

    const setMarkerRowValue = (value, row: MarkerDye, markers: string[]) => {
        setMarkerRows(markerRows.map(x => { return { ...x, value: row.key == x.key ? value : x.value } }))

        markers.forEach((item, i) => {
            onChangeMarkerValue(value, item);
        })

        if (props.onMarkersValueChange)
            props.onMarkersValueChange(row.systems.map(x => x.name), value)
    }

    const colNum = max(markerRows.map(x => x.systems.length))

    if (props.readOnly) {
        const selectedColors = groupBy(systemList, x => x.color);
        return (<span>{selectedColors != null ? Object.keys(selectedColors).map((colorKey, colorKeyIdx) => {
            const byValues = groupBy(selectedColors[colorKey].map(c => c.value), x => x);
            return <span key={colorKey}>{colorKeyIdx ? ', ' : ''}<b>{colorKey.substring(0, 1).toUpperCase()}</b>:&nbsp;{Object.keys(byValues).join(' / ')}</span>
        }) : ""}</span>)
    } else {
        return (
            <div>
                {markerRows.map((row, i) => {
                    const markers = row.systems
                    return (
                        <Table fixed color={row.color as any} key={row.key} size={'small'} compact collapsing>
                            <TableHeader fullWidth>
                                <TableRow>
                                    <TableHeaderCell width={2}>{row.color}</TableHeaderCell>
                                    {markers.map(marker => {
                                        return <TableHeaderCell width={2} key={`h${marker.name}`}>{marker.name}</TableHeaderCell>
                                    })}
                                    {[...Array(colNum - markers.length).keys()].map((marker, idx) => {
                                        return <TableHeaderCell width={2} key={`b${idx}`}></TableHeaderCell>
                                    })}
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                <TableRow>

                                    <TableCell><input onChange={e => setMarkerRowValue(e.target.value, row, row.systems.map(x => x.name))} value={row.value}></input></TableCell>

                                    {markers.map(marker => {
                                        return <TableCell key={`c${marker.name}`}><input onChange={e => onChangeMarkerValue(e.target.value, marker.name)} value={marker.value}></input></TableCell>
                                    })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    )
                })}
            </div>);
    }

}



